import Vue from 'vue'
import VueRouter from 'vue-router'
import logger from '@/utils/logger'
// 首页和主要布局组件保持同步加载（首屏需要）
import homeIndex from '../views/homeIndex'
import HomeView from '../views/HomeView'

// 统一规范视图路径，避免传参不一致导致生产环境找不到模块
const normalizeViewPath = (view) => {
  const viewPath = (view || '').trim().replace(/^\/+/, '')
  return viewPath.endsWith('.vue') ? viewPath : `${viewPath}.vue`
}

const resolveViewKey = (view) => `./${normalizeViewPath(view)}`
const loadErrorPage = () => import(/* webpackChunkName: "error-page" */ '../views/error/errorPage.vue')

// 使用 require.context 建立可校验模块清单，避免运行时动态路径找不到模块
const viewContext = require.context('../views', true, /\.vue$/, 'lazy')
const viewKeys = new Set(viewContext.keys())

// 懒加载辅助函数 - 先校验文件存在，再执行异步加载
const lazyLoad = (view) => {
  const viewKey = resolveViewKey(view)
  return () => {
    if (!viewKeys.has(viewKey)) {
      logger.error(`[Router] 路由组件不存在: ${viewKey}`)
      return loadErrorPage()
    }

    return viewContext(viewKey).catch((err) => {
      logger.error(`[Router] 懒加载失败: ${viewKey}`, err)
      return loadErrorPage()
    })
  }
}

// 显式懒加载：避免动态上下文在部分构建环境下丢失模块映射
const chnEngLoginView = () => import(/* webpackChunkName: "chn-eng-login" */ '../views/loginRegist/chnEngLogin.vue')

Vue.use(VueRouter)

// 禁用浏览器原生滚动位置还原
try {
  if ('scrollRestoration' in window.history) {
    window.history.scrollRestoration = 'manual'
  }
} catch (e) {}

const routes = [
  {
    path: '*',
    name: '404',
    component: lazyLoad('error/errorPage.vue')
  },
  {
    path: '/',
    component: homeIndex,
    redirect: '/HomeView',
    children: [
      { path: '/HomeView', component: HomeView },
      { path: '/globalFinals', component: lazyLoad('globalFinals.vue') },
      { path: '/eLearn', component: lazyLoad('eLearn.vue') },
      { path: '/FAQ', component: lazyLoad('faq.vue') },
      { path: '/joinUs', component: lazyLoad('joinUs.vue') },
      { path: '/aboutBackground', component: lazyLoad('aboutcebsb/aboutBackground') },
      { path: '/aboutHistory', component: lazyLoad('aboutcebsb/aboutHistory') },
      { path: '/aboutHonour', component: lazyLoad('aboutcebsb/aboutHonour') },
      { path: '/aboutVision', component: lazyLoad('aboutcebsb/aboutVision') },
      { path: '/invGameType', component: lazyLoad('comgametype/invGameType') },
      { path: '/teamGameType', component: lazyLoad('comgametype/teamGameType') },
      { path: '/schoolBeefunactivity', component: lazyLoad('forSchoolInfo/schoolBeefunactivity') },
      { path: '/schoolTeamTraining', component: lazyLoad('forSchoolInfo/schoolTeamTraining') },
      { path: '/schoolEstablishMents', component: lazyLoad('forSchoolInfo/schoolEstablishMents') },
      { path: '/schoolMemberShip', component: lazyLoad('forSchoolInfo/schoolMemberShip') },
      { path: '/speCharacterBank', component: lazyLoad('forspellers/speCharacterBank') },
      { path: '/speRegisterFirCompetition', component: lazyLoad('forspellers/speRegisterFirCompetition') },
      { path: '/speFaqs', component: lazyLoad('forspellers/speFaqs') },
      { path: '/forSchoolFaqs', component: lazyLoad('forSchoolInfo/forSchoolFaqs') },
      { path: '/speCarnyialFuture', component: lazyLoad('forspellers/speCarnyialFuture') },
      { path: '/speOnlineLearningResource', component: lazyLoad('forspellers/speOnlineLearningResource') },
      { path: '/judgeAndCoaches', component: lazyLoad('joinUsView/judgeAndCoaches') },
      { path: '/joinUsIndex', component: lazyLoad('joinUsView/joinUsIndex.vue') },
      { path: '/volunteersView', component: lazyLoad('joinUsView/volunteersView') },
      { path: '/jsonPartner', component: lazyLoad('joinUsView/jsonPartner.vue') },
      { path: '/aboutCommttee', component: lazyLoad('aboutcebsb/aboutCommttee') },
      { path: '/bilingualCourses', component: lazyLoad('homeLearnMore/bilingualCourses') },
      { path: '/competitiveCommAct', component: lazyLoad('homeLearnMore/competitiveCommAct') },
      { path: '/studyTours', component: lazyLoad('homeLearnMore/studyTours') },
      { path: '/paymentProcess', component: lazyLoad('orderPayment/paymentProcess') },
      { path: '/cebsbUserInfo', component: lazyLoad('aboutUserData/cebsbUserInfo') },
      { path: '/schoolIndividualSignUp', component: lazyLoad('forSchoolInfo/schoolIndividualSignUp') },
      { path: '/userInfo_IndividualPapersDetail', component: lazyLoad('aboutUserData/cebsbIndividualInfoView/userInfo_IndividualPapersDetail') },
      {
        path: '/cebsbInfoData',
        component: lazyLoad('aboutUserData/cebsbInfoData'),
        redirect: '/userInfo_welcome',
        children: [
          { path: '/userInfo_welcome', component: lazyLoad('aboutUserData/userInfoWelcome') },
          { path: '/userInfo_schoolData', component: lazyLoad('aboutUserData/cebsbSchoolInfoView/userInfo_schoolData') },
          { path: '/userInfo_personalData', component: lazyLoad('aboutUserData/cebsbSchoolInfoView/userInfo_personalData') },
          { path: '/userInfo_IndividualPapers', component: lazyLoad('aboutUserData/cebsbIndividualInfoView/userInfo_IndividualPapers') },
          { path: '/userInfo_individualScore', component: lazyLoad('aboutUserData/cebsbIndividualInfoView/userInfo_individualScore') },
          { path: '/userInfo_individualHistory', component: lazyLoad('aboutUserData/cebsbIndividualInfoView/userInfo_individualHistory') },
          { path: '/userInfo_teamManagement', component: lazyLoad('aboutUserData/cebsbTeamInfoView/userInfo_teamManagement') },
          { path: '/userInfo_teamProcessMembers', component: lazyLoad('aboutUserData/cebsbTeamInfoView/userInfo_teamProcessMembers') },
          { path: '/userInfo_teamScoreSearch', component: lazyLoad('aboutUserData/cebsbTeamInfoView/userInfo_teamScoreSearch') },
          { path: '/userInfo_teamHistoryScoreSearch', component: lazyLoad('aboutUserData/cebsbTeamInfoView/userInfo_teamHistoryScoreSearch') },
          { path: '/userInfo_rightsManagementData', component: lazyLoad('aboutUserData/cebsbSchoolInfoView/userInfo_rightsManagementData') },
          { path: '/cebsbInfo_communityMember', component: lazyLoad('aboutUserData/cebsbCommunityInfoView/cebsbInfo_communityMember') },
          { path: '/aboutUserData/cebsbCommunityInfoView/cebsbCourseManage/cebsbInfo_community_courseMember', component: lazyLoad('aboutUserData/cebsbCommunityInfoView/cebsbCourseManage/cebsbInfo_community_courseMember') },
          { path: '/aboutUserData/cebsbCommunityInfoView/cebsbCourseManage/cebsbInfo_community_courseClass', component: lazyLoad('aboutUserData/cebsbCommunityInfoView/cebsbCourseManage/cebsbInfo_community_courseClass') },
          { path: '/aboutUserData/cebsbCommunityInfoView/cebsbCourseManage/cebsbInfo_community_courseDetail', component: lazyLoad('aboutUserData/cebsbCommunityInfoView/cebsbCourseManage/cebsbInfo_community_courseDetail') },
          { path: '/aboutUserData/cebsbCommunityInfoView/cebsbCourseManage/cebsbInfo_user_courseMember', component: lazyLoad('aboutUserData/cebsbCommunityInfoView/cebsbCourseManage/cebsbInfo_user_courseMember') },
          { path: '/aboutUserData/cebsbCommunityInfoView/cebsbCourseManage/cebsbInfo_user_bindSchoolData', component: lazyLoad('aboutUserData/cebsbCommunityInfoView/cebsbCourseManage/cebsbInfo_user_bindSchoolData') },
          { path: '/userInfoIndividualPapersDetailNew', component: lazyLoad('aboutUserData/cebsbIndividualInfoView/userInfoIndividualPapersDetailNew') }
        ]
      }
    ]
  },
  {
    path: '/cebsbCourseManage/cebso_onlineBrowsingPDF', component: lazyLoad('aboutUserData/cebsbCommunityInfoView/cebsbCourseManage/cebso_onlineBrowsingPDF')
  },
  {
    path: '/cebsbCourseManage/cebso_onlineBrowsingVideo', component: lazyLoad('aboutUserData/cebsbCommunityInfoView/cebsbCourseManage/cebso_onlineBrowsingVideo')
  },
  { path: '/chnEngLogin', component: chnEngLoginView },
  { path: '/teacherLogin', component: lazyLoad('loginRegist/chnEngTeacherLogin.vue') },
  { path: '/chnEngRegist', component: lazyLoad('loginRegist/chnEngRegist.vue') },
  { path: '/resetPwd', component: lazyLoad('loginRegist/resetPwd.vue') },
  { path: '/stuReg', component: lazyLoad('loginRegist/stuRegister.vue') },
  // 机构登录相关路由
  { path: '/partnerLogin', component: lazyLoad('loginRegist/partnerLogin.vue') },
  { path: '/partner/forgot-password', component: lazyLoad('loginRegist/partnerForgotPassword.vue') },
  { path: '/partner/reset-password', component: lazyLoad('loginRegist/partnerResetPassword.vue') },
  { path: '/partner/apply-registration', component: lazyLoad('loginRegist/partnerApplyRegistration.vue') },
  // 机构管理后台路由
  {
    path: '/partner',
    component: lazyLoad('partner/dashboard.vue'),
    redirect: '/partner/dashboard',
    children: [
      { path: 'dashboard', component: lazyLoad('partner/dashboardHome.vue') },
      { path: 'school-manage', component: lazyLoad('partner/schoolManage.vue') },
      { path: 'classManage', component: lazyLoad('partner/classManage.vue') },
      { path: 'studentsManage', component: lazyLoad('partner/studentsManage.vue') },
      { path: 'studentManage', component: lazyLoad('partner/studentManage.vue') },
      { path: 'trialClassManage', component: lazyLoad('partner/trialClassManage.vue') },
      { path: 'trialClassEdit', component: lazyLoad('partner/trialClassEdit.vue') },
      { path: 'matchList', component: lazyLoad('partner/matchList.vue') },
      { path: 'matchGrouping', component: lazyLoad('partner/matchGroup.vue') },
      { path: 'matchPaper', component: lazyLoad('partner/matchPaper.vue') },
      { path: 'matchControl', component: lazyLoad('partner/matchControl.vue') },
      { path: 'exam-system', component: lazyLoad('partner/examSystem.vue') },
      { path: 'judge-system', component: lazyLoad('partner/judgeSystem.vue') },
      { path: 'player-info', component: lazyLoad('partner/playerInfo.vue') },
      { path: 'resource-tools', component: lazyLoad('partner/resourceTools.vue') },
      { path: 'codeManagement', component: lazyLoad('partner/examSystemChild/codeManagement.vue') },
      { path: 'userSignUpList', component: lazyLoad('partner/examSystemChild/userSignUpList.vue') },
      { path: 'globalFinalList', component: lazyLoad('partner/examSystemChild/globalFinal.vue') },
      { path: 'player_registInfo_details', component: lazyLoad('partner/examSystemChild/player_registInfo_details.vue') },
      { path: 'examPaperManagement', component: lazyLoad('partner/examMan/examPaper.vue') },
      { path: 'examPaperEdit', component: lazyLoad('partner/examMan/examPaperEdit.vue') },
      { path: 'examPaperDownLoad', component: lazyLoad('partner/examMan/examPaperDownLoad.vue') },
      { path: 'examTimeManagement', component: lazyLoad('partner/examMan/examTimeManage.vue') }
    ]
  },
  { path: '/query', component: lazyLoad('certificateQuery.vue') },
  { path: '/certificate', component: lazyLoad('certificateResult.vue') },
  {
    path: '/stu',
    component: lazyLoad('stuPage/stuContainer.vue'),
    redirect: '/stu/dashboard',
    children: [
      { path: '/stu/dashboard', component: lazyLoad('stuPage/stuDashboard.vue') },
      { path: '/stu/index', component: lazyLoad('stuPage/indexPage.vue') },
      { path: '/stu/placeOrder', component: lazyLoad('stuPage/placeOrder.vue') },
      { path: '/stu/lexicon/index', component: lazyLoad('stuPage/lexicon/lexiconIndex.vue') },
      { path: '/stu/lexicon/directory', component: lazyLoad('stuPage/lexicon/lexiconDirectory.vue') },
      { path: '/stu/lexicon/font', component: lazyLoad('stuPage/lexicon/lexiconFont.vue') },
      { path: '/stu/lexicon/trialClass', component: lazyLoad('stuPage/lexicon/trialClass.vue') },
      { path: '/stu/lexicon/trialFont', component: lazyLoad('stuPage/lexicon/trialFont.vue') },
      { path: '/stu/exam/be', component: lazyLoad('stuPage/exam/beforeEnter.vue') },
      { path: '/stu/exam/quiz', component: lazyLoad('stuPage/exam/onlineQuizzes.vue') },
      { path: '/semilogin', component: lazyLoad('stuPage/examSemi/stuSemiWebLogin.vue') },
      { path: '/stu/examSemi/be', component: lazyLoad('stuPage/examSemi/beforeEnter.vue') },
      { path: '/stu/examSemi/quiz', component: lazyLoad('stuPage/examSemi/onlineQuizzes.vue') },
      { path: '/stu/examSemi/result', component: lazyLoad('stuPage/examSemi/resultPage.vue') },
      { path: '/stu/user', component: lazyLoad('stuPage/userInfo.vue') },
      { path: '/stu/exer/index', component: lazyLoad('stuPage/exercise/exerIndex.vue') },
      { path: '/stu/exer/answer', component: lazyLoad('stuPage/exercise/exerAnswer.vue') },
      { path: '/stu/exer/result', component: lazyLoad('stuPage/exercise/exerResult.vue') },
      { path: '/stu/message/list', component: lazyLoad('stuPage/messageList.vue') },
      { path: '/stu/score/result', component: lazyLoad('stuPage/scoreResult.vue') }
    ]
  },
  {
    path: 'padH5',
    component: lazyLoad('padH5/containerH5.vue'),
    children: [
      { path: '/pad/matchQuiz', component: lazyLoad('padH5/match/matchQuiz.vue') }
    ]
  }
]

const router = new VueRouter({
  routes,
  scrollBehavior (_to, _from, _savedPosition) {
    // 期望滚动到哪个位置
    return {
      x: 0,
      y: 0
    }
  }
})
export default router
const VueRouterPush = VueRouter.prototype.push
VueRouter.prototype.push = function push (to) {
  return VueRouterPush.call(this, to).catch(err => err)
}

// 确保每次路由切换后滚动到页面顶部（兜底处理）
router.afterEach((_to, _from) => {
  // 立即重置 window 和 document 的滚动位置
  try {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
    document.body.scrollTop = 0
    document.documentElement.scrollTop = 0
  } catch (e) {
    window.scrollTo(0, 0)
  }

  // 重置滚动容器的函数
  const resetScrollers = () => {
    try {
      // 重置 .top-backview 滚动容器
      const topBackviews = document.querySelectorAll('.top-backview')
      topBackviews.forEach(el => {
        try {
          el.scrollTop = 0
          if (typeof el.scrollTo === 'function') {
            el.scrollTo({ top: 0, left: 0, behavior: 'auto' })
          }
        } catch (e) {
          logger.warn('重置 .top-backview 滚动失败:', e)
        }
      })

      // 重置 el-main 滚动容器（homeIndex 和 stuContainer 使用）
      const elMains = document.querySelectorAll('.el-main')
      elMains.forEach(el => {
        try {
          el.scrollTop = 0
          if (typeof el.scrollTo === 'function') {
            el.scrollTo({ top: 0, left: 0, behavior: 'auto' })
          }
        } catch (e) {
          logger.warn('重置 .el-main 滚动失败:', e)
        }
      })

      // 重置其他可能的滚动容器
      const allScrollableElements = document.querySelectorAll('[style*="overflow"], [class*="scroll"]')
      allScrollableElements.forEach(el => {
        try {
          const computedStyle = window.getComputedStyle(el)
          if (computedStyle.overflow === 'auto' || computedStyle.overflow === 'scroll' ||
              computedStyle.overflowY === 'auto' || computedStyle.overflowY === 'scroll') {
            el.scrollTop = 0
            if (typeof el.scrollTo === 'function') {
              el.scrollTo({ top: 0, left: 0, behavior: 'auto' })
            }
          }
        } catch (e) {
          // 忽略错误，继续处理下一个元素
        }
      })
    } catch (e) {
      logger.warn('重置滚动容器时出错:', e)
    }
  }

  // 使用多个延迟确保在不同渲染阶段都能重置滚动
  // 第一次：立即尝试（针对已存在的元素）
  resetScrollers()

  // 第二次：等待 DOM 更新（Vue 的 nextTick 等价）
  setTimeout(() => {
    resetScrollers()
  }, 0)

  // 第三次：等待组件完全渲染（延迟更长）
  setTimeout(() => {
    resetScrollers()
  }, 100)

  // 第四次：最终兜底（确保所有异步渲染完成）
  setTimeout(() => {
    resetScrollers()
    // 最后再次确保 window 滚动位置
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
    document.body.scrollTop = 0
    document.documentElement.scrollTop = 0
  }, 300)
})
